import React, { useState, useEffect } from 'react';
import {
  Box,
  CircularProgress,
  LinearProgress,
  Typography,
  Card,
  CardContent,
  Button,
  Grid,
  Chip,
  Alert,
  TextField,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Avatar,
  Badge,
  IconButton,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Checkbox,
  InputAdornment,
  Paper,
} from '@mui/material';
import {
  WhatsApp as WhatsAppIcon,
  QrCode as QrCodeIcon,
  Refresh as RefreshIcon,
  Send as SendIcon,
  PowerSettingsNew as PowerIcon,
  CheckCircle as CheckIcon,
  Error as ErrorIcon,
  Warning as WarningIcon,
  Add as AddIcon,
  Delete as DeleteIcon,
  Group as GroupIcon,
  Phone as PhoneIcon,
  TableRows as TableRowsIcon,
  Search as SearchIcon,
} from '@mui/icons-material';
import { useSocket } from '../contexts/SocketContext';
import { whatsappAccountService, WhatsAppAccount } from '../services/whatsappAccountService';
import toast from 'react-hot-toast';

const WhatsappAccounts: React.FC = () => {
  const [loading, setLoading] = useState(false);
  const [whatsappAccounts, setWhatsappAccounts] = useState<WhatsAppAccount[]>([]);
  const [accountDialogOpen, setAccountDialogOpen] = useState(false);
  const [qrDialogOpen, setQrDialogOpen] = useState(false);
  const [testDialogOpen, setTestDialogOpen] = useState(false);
  const [selectedAccountForQR, setSelectedAccountForQR] = useState<WhatsAppAccount | null>(null);
  const [qrSecondsLeft, setQrSecondsLeft] = useState(30);
  const [qrRefreshing, setQrRefreshing] = useState(false);
  const [selectedAccountForTest, setSelectedAccountForTest] = useState<WhatsAppAccount | null>(null);
  const [testNumber, setTestNumber] = useState('');
  const [testMessage, setTestMessage] = useState('Hello! This is a test message from Taxi Leads system.');
  const [accountForm, setAccountForm] = useState({
    accountName: '',
    phoneNumber: '',
  });
  const [groupTableDialogOpen, setGroupTableDialogOpen] = useState(false);
  const [groupTableAccount, setGroupTableAccount] = useState<WhatsAppAccount | null>(null);
  const [groupTableSearch, setGroupTableSearch] = useState('');
  const [groupTableSelected, setGroupTableSelected] = useState<string[]>([]);
  const [modalGroups, setModalGroups] = useState<{ id: string; name: string }[]>([]);
  const [modalGroupsLoading, setModalGroupsLoading] = useState(false);
  const { socket } = useSocket();

  useEffect(() => {
    loadWhatsAppAccounts();
    // Refresh accounts every 10 seconds
    const interval = setInterval(loadWhatsAppAccounts, 10000);
    return () => clearInterval(interval);
  }, []);

  // Listen for WhatsApp account status changes via socket
  useEffect(() => {
    if (!socket) return;

    const handleAccountStatusUpdate = (data: any) => {
      console.log('WhatsApp account status update:', data);
      
      // Close QR dialog if the account is authenticated or ready and we have QR dialog open for this account
      if (data.sessionId && selectedAccountForQR && selectedAccountForQR.sessionId === data.sessionId) {
        // Close QR modal when account becomes authenticated (connected) or ready
        if (data.isAuthenticated || data.isConnected || data.isReady) {
          const accountName = selectedAccountForQR.accountName;
          setQrDialogOpen(false);
          setSelectedAccountForQR(null);
          if (data.isReady) {
            toast.success(`${accountName} is ready for authorized ride channels.`);
          } else if (data.isAuthenticated || data.isConnected) {
            toast.success(`${accountName} authenticated successfully! Setting up...`);
          }
        }
      }
      
      // Refresh accounts to get latest status
      loadWhatsAppAccounts();
    };

    socket.on('whatsapp:accountStatusUpdate', handleAccountStatusUpdate);
    socket.on('whatsapp:accountReady', handleAccountStatusUpdate);
    socket.on('whatsapp:statusChanged', handleAccountStatusUpdate);

    return () => {
      socket.off('whatsapp:accountStatusUpdate', handleAccountStatusUpdate);
      socket.off('whatsapp:accountReady', handleAccountStatusUpdate);
      socket.off('whatsapp:statusChanged', handleAccountStatusUpdate);
    };
  }, [socket, selectedAccountForQR]);

  const loadWhatsAppAccounts = async () => {
    try {
      const accounts = await whatsappAccountService.getAccounts();
      setWhatsappAccounts(accounts);
    } catch (error) {
      console.error('Error loading WhatsApp accounts:', error);
      toast.error('Failed to load WhatsApp accounts');
    }
  };

  const refreshAccountGroups = async (account: WhatsAppAccount) => {
    try {
      setLoading(true);

      if (!account.isReady) {
        toast.error('Account must be ready before groups can be refreshed');
        return;
      }

      const groups = await whatsappAccountService.getAccountGroups(account.sessionId);

      setWhatsappAccounts((previousAccounts) =>
        previousAccounts.map((existingAccount) =>
          existingAccount.sessionId === account.sessionId
            ? { ...existingAccount, groups }
            : existingAccount
        )
      );

      toast.success(`Loaded ${groups.length} WhatsApp group${groups.length === 1 ? '' : 's'}`);
      await loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error refreshing ride groups:', error);
      toast.error('Failed to refresh ride groups');
    } finally {
      setLoading(false);
    }
  };

  const createWhatsAppAccount = async () => {
    try {
      setLoading(true);
      await whatsappAccountService.createAccount(accountForm);
      toast.success('WhatsApp account created successfully');
      setAccountDialogOpen(false);
      setAccountForm({ accountName: '', phoneNumber: '' });
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error creating WhatsApp account:', error);
      toast.error('Failed to create WhatsApp account');
    } finally {
      setLoading(false);
    }
  };

  const connectWhatsAppAccount = async (sessionId: string) => {
    try {
      setLoading(true);
      await whatsappAccountService.connectAccount(sessionId);
      toast.success('Connection initiated. Check QR code if needed.');
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error connecting WhatsApp account:', error);
      toast.error('Failed to connect WhatsApp account');
    } finally {
      setLoading(false);
    }
  };

  const disconnectWhatsAppAccount = async (sessionId: string) => {
    try {
      setLoading(true);
      await whatsappAccountService.disconnectAccount(sessionId);
      toast.success('WhatsApp account disconnected');
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error disconnecting WhatsApp account:', error);
      toast.error('Failed to disconnect WhatsApp account');
    } finally {
      setLoading(false);
    }
  };

  const reinitializeWhatsAppAccount = async (sessionId: string) => {
    try {
      setLoading(true);
      await whatsappAccountService.reinitializeAccount(sessionId);
      toast.success('Reinitializing connection — this may take up to 60 seconds.');
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error reinitializing WhatsApp account:', error);
      toast.error('Failed to reinitialize. Try disconnecting and reconnecting.');
    } finally {
      setLoading(false);
    }
  };

  const deleteWhatsAppAccount = async (sessionId: string) => {
    if (!window.confirm('Are you sure you want to delete this WhatsApp account?')) {
      return;
    }
    
    try {
      setLoading(true);
      await whatsappAccountService.deleteAccount(sessionId);
      toast.success('WhatsApp account deleted');
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error deleting WhatsApp account:', error);
      toast.error('Failed to delete WhatsApp account');
    } finally {
      setLoading(false);
    }
  };

  const refreshQRCode = async (account: WhatsAppAccount) => {
    setQrRefreshing(true);
    try {
      const status = await whatsappAccountService.getAccountStatus(account.sessionId);
      if (status?.qrCode) {
        setSelectedAccountForQR({ ...account, qrCode: status.qrCode });
        setQrSecondsLeft(30);
      }
    } catch {
      // silently ignore — will auto-retry next cycle
    } finally {
      setQrRefreshing(false);
    }
  };

  useEffect(() => {
    if (!qrDialogOpen || !selectedAccountForQR) return;
    setQrSecondsLeft(30);
    const countdown = setInterval(() => {
      setQrSecondsLeft((s) => {
        if (s <= 1) {
          refreshQRCode(selectedAccountForQR);
          return 30;
        }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(countdown);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [qrDialogOpen, selectedAccountForQR?.sessionId]);

  const showQRCode = async (account: WhatsAppAccount) => {
    try {
      const status = await whatsappAccountService.getAccountStatus(account.sessionId);
      if (status?.qrCode) {
        setSelectedAccountForQR({ ...account, qrCode: status.qrCode });
        setQrDialogOpen(true);
        setQrSecondsLeft(30);
      } else {
        toast.error('QR code not available. Try connecting the account first.');
      }
    } catch (error) {
      console.error('Error getting QR code:', error);
      toast.error('Failed to get QR code');
    }
  };

  const sendTestMessage = async () => {
    if (!selectedAccountForTest || !testNumber.trim() || !testMessage.trim()) {
      toast.error('Please select account and enter both phone number and message');
      return;
    }

    try {
      setLoading(true);
      await whatsappAccountService.sendMessage(selectedAccountForTest.sessionId, testNumber, testMessage);
      toast.success('Test message sent successfully');
      setTestDialogOpen(false);
      setTestNumber('');
    } catch (error) {
      console.error('Error sending test message:', error);
      toast.error('Failed to send test message');
    } finally {
      setLoading(false);
    }
  };

  const updateAccountGroups = async (sessionId: string, selectedGroups: string[]) => {
    try {
      const account = whatsappAccounts.find(acc => acc.sessionId === sessionId);
      if (!account) return;

      await whatsappAccountService.updateAccountSettings(sessionId, {
        ...account.settings,
        monitoredGroups: selectedGroups,
      });

      toast.success('Account groups updated');
      loadWhatsAppAccounts();
    } catch (error) {
      console.error('Error updating account groups:', error);
      toast.error('Failed to update account groups');
    }
  };

  const openGroupTableDialog = async (account: WhatsAppAccount) => {
    setGroupTableAccount(account);
    setGroupTableSelected(account.settings?.monitoredGroups || []);
    setGroupTableSearch('');
    setModalGroups([]);
    setModalGroupsLoading(true);
    setGroupTableDialogOpen(true);
    try {
      const groups = await whatsappAccountService.getAccountGroups(account.sessionId);
      setModalGroups(groups);
    } catch {
      toast.error('Failed to load groups');
    } finally {
      setModalGroupsLoading(false);
    }
  };

  const saveGroupTableSelection = async () => {
    if (!groupTableAccount) return;
    await updateAccountGroups(groupTableAccount.sessionId, groupTableSelected);
    setGroupTableDialogOpen(false);
  };

  const toggleGroupInTable = (groupId: string) => {
    setGroupTableSelected(prev =>
      prev.indexOf(groupId) !== -1 ? prev.filter(id => id !== groupId) : [...prev, groupId]
    );
  };

  const toggleSelectAll = (visibleIds: string[]) => {
    const allSelected = visibleIds.every(id => groupTableSelected.indexOf(id) !== -1);
    if (allSelected) {
      setGroupTableSelected(prev => prev.filter(id => visibleIds.indexOf(id) === -1));
    } else {
      setGroupTableSelected(prev =>
        prev.concat(visibleIds.filter(id => prev.indexOf(id) === -1))
      );
    }
  };

  const getStatusIcon = (account: WhatsAppAccount) => {
    if (account.isReady) {
      return <CheckIcon color="success" />;
    } else if (account.isConnected) {
      return <WarningIcon color="warning" />;
    } else {
      return <ErrorIcon color="error" />;
    }
  };

  const getStatusText = (account: WhatsAppAccount) => {
    if (account.isReady) {
      return 'Ready';
    } else if (account.isConnected) {
      return 'Connected - Setup Required';
    } else {
      return 'Disconnected';
    }
  };

  const getStatusColor = (account: WhatsAppAccount) => {
    if (account.isReady) return 'success';
    if (account.isConnected) return 'warning';
    return 'error';
  };

  const formatLastActivity = (lastActivity?: Date | string) => {
    if (!lastActivity) return 'Never';
    const date = typeof lastActivity === 'string' ? new Date(lastActivity) : lastActivity;
    return date.toLocaleString();
  };

  return (
    <Box sx={{ p: 3 }}>
      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 3 }}>
        <Typography variant="h4">
          WhatsApp Multi-Account Management
        </Typography>
        <Button
          variant="contained"
          startIcon={<AddIcon />}
          onClick={() => setAccountDialogOpen(true)}
          size="large"
        >
          Add Account
        </Button>
      </Box>

      {/* Overview Stats */}
      <Grid container spacing={3} sx={{ mb: 3 }}>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="primary">Total Accounts</Typography>
              <Typography variant="h3">{whatsappAccounts.length}</Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="success.main">Active & Ready</Typography>
              <Typography variant="h3">
                {whatsappAccounts.filter(acc => acc.isReady).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="warning.main">Needs Setup</Typography>
              <Typography variant="h3">
                {whatsappAccounts.filter(acc => acc.isConnected && !acc.isReady).length}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
        <Grid item xs={12} md={3}>
          <Card>
            <CardContent>
              <Typography variant="h6" color="info.main">Monitored Groups</Typography>
              <Typography variant="h3">
                {whatsappAccounts.reduce((total, acc) => total + (acc.settings?.monitoredGroups?.length || 0), 0)}
              </Typography>
            </CardContent>
          </Card>
        </Grid>
      </Grid>

      {/* Accounts List */}
      {whatsappAccounts.length === 0 ? (
        <Alert severity="info" sx={{ mb: 3 }}>
          No WhatsApp accounts configured. Add your first account to start receiving leads from multiple WhatsApp numbers.
        </Alert>
      ) : (
        <Grid container spacing={3}>
          {whatsappAccounts.map((account) => (
            <Grid item xs={12} lg={6} key={account.sessionId}>
              <Card sx={{ height: '100%' }}>
                <CardContent>
                  {/* Account Header */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    <Badge
                      color={getStatusColor(account) as any}
                      variant="dot"
                      overlap="circular"
                    >
                      <Avatar sx={{ bgcolor: 'primary.main', mr: 2 }}>
                        <WhatsAppIcon />
                      </Avatar>
                    </Badge>
                    <Box sx={{ flex: 1 }}>
                      <Typography variant="h6">{account.accountName}</Typography>
                      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                        <PhoneIcon fontSize="small" color="action" />
                        <Typography variant="body2" color="text.secondary">
                          {account.phoneNumber}
                        </Typography>
                      </Box>
                    </Box>
                    <IconButton
                      size="small"
                      onClick={() => account.isReady ? refreshAccountGroups(account) : reinitializeWhatsAppAccount(account.sessionId)}
                      title={account.isReady ? 'Refresh groups' : 'Reinitialize connection'}
                      disabled={loading}
                    >
                      <RefreshIcon />
                    </IconButton>
                  </Box>

                  {/* Status */}
                  <Box sx={{ display: 'flex', alignItems: 'center', mb: 2 }}>
                    {getStatusIcon(account)}
                    <Typography variant="subtitle1" sx={{ ml: 1 }}>
                      {getStatusText(account)}
                    </Typography>
                  </Box>

                  <Box sx={{ mb: 2, display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
                    <Chip
                      label={account.isReady ? 'Active' : account.isConnected ? 'Setup Needed' : 'Offline'}
                      color={getStatusColor(account) as any}
                      size="small"
                    />
                    {account.settings?.monitoredGroups?.length > 0 && (
                      <Chip
                        label={`${account.settings.monitoredGroups.length} groups selected`}
                        color="info"
                        size="small"
                        icon={<GroupIcon />}
                      />
                    )}
                  </Box>

                  {/* Status Details */}
                  <Box sx={{ mb: 2 }}>
                    <Typography variant="body2" color="text.secondary">
                      Last Activity: {formatLastActivity(account.lastActivity)}
                    </Typography>
                  </Box>

                  {/* Actions */}
                  <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', mb: 2 }}>
                    {!account.isConnected ? (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<PowerIcon />}
                        onClick={() => connectWhatsAppAccount(account.sessionId)}
                        disabled={loading}
                      >
                        Connect
                      </Button>
                    ) : (
                      <Button
                        size="small"
                        variant="outlined"
                        color="warning"
                        startIcon={<PowerIcon />}
                        onClick={() => disconnectWhatsAppAccount(account.sessionId)}
                        disabled={loading}
                      >
                        Disconnect
                      </Button>
                    )}
                    
                    {account.qrCode && (
                      <Button
                        size="small"
                        variant="outlined"
                        startIcon={<QrCodeIcon />}
                        onClick={() => showQRCode(account)}
                      >
                        QR Code
                      </Button>
                    )}

                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<SendIcon />}
                      onClick={() => {
                        setSelectedAccountForTest(account);
                        setTestDialogOpen(true);
                      }}
                      disabled={!account.isReady}
                    >
                      Test
                    </Button>

                    <Button
                      size="small"
                      variant="outlined"
                      color="error"
                      startIcon={<DeleteIcon />}
                      onClick={() => deleteWhatsAppAccount(account.sessionId)}
                      disabled={loading}
                    >
                      Delete
                    </Button>
                  </Box>

                  {/* Groups Management — lazy loaded in modal */}
                  <Box sx={{ mt: 2, display: 'flex', alignItems: 'center', justifyContent: 'space-between' }}>
                    <Typography variant="body2" color="text.secondary">
                      {account.settings?.monitoredGroups?.length
                        ? `${account.settings.monitoredGroups.length} group${account.settings.monitoredGroups.length !== 1 ? 's' : ''} selected`
                        : 'No groups selected yet'}
                    </Typography>
                    <Button
                      size="small"
                      variant="outlined"
                      startIcon={<TableRowsIcon />}
                      onClick={() => openGroupTableDialog(account)}
                      disabled={!account.isReady}
                    >
                      Manage Groups
                    </Button>
                  </Box>

                  {/* Status-specific alerts */}
                  {!account.isConnected && (
                    <Alert severity="error" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Action Required:</strong> Click "Connect" to link this WhatsApp account.
                      </Typography>
                    </Alert>
                  )}

                  {account.isConnected && !account.isReady && !account.qrCode && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Not Ready:</strong> WhatsApp is connected but the backend session isn't fully active. Click the refresh icon to reinitialize without scanning QR again.
                      </Typography>
                    </Alert>
                  )}

                  {account.isConnected && !account.isReady && account.qrCode && (
                    <Alert severity="warning" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Setup Required:</strong> Scan QR code with WhatsApp on your phone to complete setup.
                      </Typography>
                    </Alert>
                  )}

                  {account.isReady && (!account.settings?.monitoredGroups?.length || account.settings.monitoredGroups.length === 0) && (
                    <Alert severity="info" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Configure Channels:</strong> Select authorized ride groups below to organize ride requests.
                      </Typography>
                    </Alert>
                  )}

                  {account.isReady && account.settings?.monitoredGroups?.length > 0 && (
                    <Alert severity="success" sx={{ mt: 2 }}>
                      <Typography variant="body2">
                        <strong>Active:</strong> {account.settings.monitoredGroups.length} group(s) selected for ride requests.
                      </Typography>
                    </Alert>
                  )}
                </CardContent>
              </Card>
            </Grid>
          ))}
        </Grid>
      )}

      {/* Create Account Dialog */}
      <Dialog open={accountDialogOpen} onClose={() => setAccountDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>Add WhatsApp Account</DialogTitle>
        <DialogContent>
          <Grid container spacing={2} sx={{ mt: 1 }}>
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Account Name"
                value={accountForm.accountName}
                onChange={(e) => setAccountForm(prev => ({ ...prev, accountName: e.target.value }))}
                placeholder="e.g., Business Account, Personal Account"
              />
            </Grid>
            
            <Grid item xs={12}>
              <TextField
                fullWidth
                label="Phone Number"
                value={accountForm.phoneNumber}
                onChange={(e) => setAccountForm(prev => ({ ...prev, phoneNumber: e.target.value }))}
                placeholder="e.g., +1234567890"
              />
            </Grid>
          </Grid>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setAccountDialogOpen(false)}>Cancel</Button>
          <Button 
            onClick={createWhatsAppAccount} 
            variant="contained"
            disabled={!accountForm.accountName || !accountForm.phoneNumber || loading}
          >
            {loading ? 'Creating...' : 'Create Account'}
          </Button>
        </DialogActions>
      </Dialog>

      {/* QR Code Dialog */}
      <Dialog open={qrDialogOpen} onClose={() => { setQrDialogOpen(false); setSelectedAccountForQR(null); }} maxWidth="sm" fullWidth>
        <DialogTitle>
          Scan QR Code — {selectedAccountForQR?.accountName}
        </DialogTitle>
        <DialogContent>
          {selectedAccountForQR && (
            <Box display="flex" flexDirection="column" alignItems="center" p={2} gap={2}>
              <Typography variant="body1" textAlign="center">
                Open WhatsApp on your phone ({selectedAccountForQR.phoneNumber}) and scan this QR code to connect.
              </Typography>
              <Box position="relative" display="inline-flex">
                {qrRefreshing && (
                  <Box position="absolute" top={0} right={0} bottom={0} left={0} display="flex" alignItems="center" justifyContent="center" bgcolor="rgba(255,255,255,0.8)" zIndex={1} borderRadius={1}>
                    <CircularProgress size={40} />
                  </Box>
                )}
                {selectedAccountForQR.qrCode ? (
                  <img
                    src={selectedAccountForQR.qrCode}
                    alt="WhatsApp QR Code"
                    style={{ maxWidth: '100%', height: 'auto', maxHeight: '300px', display: 'block' }}
                  />
                ) : (
                  <Box width={250} height={250} bgcolor="grey.200" display="flex" alignItems="center" justifyContent="center">
                    <CircularProgress />
                  </Box>
                )}
              </Box>
              <Box width="100%">
                <Box display="flex" justifyContent="space-between" mb={0.5}>
                  <Typography variant="caption" color="text.secondary">QR refreshes in {qrSecondsLeft}s</Typography>
                  <Button
                    size="small"
                    startIcon={<RefreshIcon />}
                    onClick={() => refreshQRCode(selectedAccountForQR)}
                    disabled={qrRefreshing}
                  >
                    Refresh now
                  </Button>
                </Box>
                <LinearProgress variant="determinate" value={(qrSecondsLeft / 30) * 100} sx={{ borderRadius: 1 }} />
              </Box>
              <Typography variant="body2" color="text.secondary" textAlign="center">
                Keep this window open and scan with WhatsApp &rsaquo; Settings &rsaquo; Linked Devices
              </Typography>
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => { setQrDialogOpen(false); setSelectedAccountForQR(null); }}>Close</Button>
        </DialogActions>
      </Dialog>

      {/* Group Table Dialog */}
      <Dialog
        open={groupTableDialogOpen}
        onClose={() => setGroupTableDialogOpen(false)}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Manage Groups — {groupTableAccount?.accountName}
        </DialogTitle>
        <DialogContent>
          {modalGroupsLoading ? (
            <Box display="flex" flexDirection="column" alignItems="center" justifyContent="center" py={6} gap={2}>
              <CircularProgress />
              <Typography variant="body2" color="text.secondary">Loading groups…</Typography>
            </Box>
          ) : (
            <>
              <Box sx={{ mb: 2 }}>
                <TextField
                  fullWidth
                  size="small"
                  placeholder={`Search ${modalGroups.length} groups…`}
                  value={groupTableSearch}
                  onChange={(e) => setGroupTableSearch(e.target.value)}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <SearchIcon fontSize="small" />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>
              {(() => {
                const filtered = modalGroups.filter(g =>
                  !groupTableSearch || g.name.toLowerCase().indexOf(groupTableSearch.toLowerCase()) !== -1
                );
                const filteredIds = filtered.map(g => g.id);
                const allVisibleSelected = filteredIds.length > 0 && filteredIds.every(id => groupTableSelected.indexOf(id) !== -1);
                return (
                  <TableContainer component={Paper} variant="outlined" sx={{ maxHeight: 420 }}>
                    <Table size="small" stickyHeader>
                      <TableHead>
                        <TableRow>
                          <TableCell padding="checkbox">
                            <Checkbox
                              checked={allVisibleSelected}
                              indeterminate={!allVisibleSelected && filteredIds.some(id => groupTableSelected.indexOf(id) !== -1)}
                              onChange={() => toggleSelectAll(filteredIds)}
                            />
                          </TableCell>
                          <TableCell>Group Name</TableCell>
                          <TableCell>Group ID</TableCell>
                          <TableCell align="center">Monitor</TableCell>
                        </TableRow>
                      </TableHead>
                      <TableBody>
                        {filtered.map(group => {
                          const checked = groupTableSelected.indexOf(group.id) !== -1;
                          return (
                            <TableRow
                              key={group.id}
                              hover
                              selected={checked}
                              onClick={() => toggleGroupInTable(group.id)}
                              sx={{ cursor: 'pointer' }}
                            >
                              <TableCell padding="checkbox">
                                <Checkbox checked={checked} onChange={() => toggleGroupInTable(group.id)} onClick={e => e.stopPropagation()} />
                              </TableCell>
                              <TableCell>
                                <Typography variant="body2" fontWeight={checked ? 600 : 400}>
                                  {group.name}
                                </Typography>
                              </TableCell>
                              <TableCell>
                                <Typography variant="caption" color="text.secondary">
                                  {group.id}
                                </Typography>
                              </TableCell>
                              <TableCell align="center">
                                {checked && <Chip label="Selected" color="success" size="small" />}
                              </TableCell>
                            </TableRow>
                          );
                        })}
                        {filtered.length === 0 && (
                          <TableRow>
                            <TableCell colSpan={4} align="center">
                              <Typography variant="body2" color="text.secondary" sx={{ py: 2 }}>
                                {modalGroups.length === 0 ? 'No groups found for this account.' : 'No groups match your search.'}
                              </Typography>
                            </TableCell>
                          </TableRow>
                        )}
                      </TableBody>
                    </Table>
                  </TableContainer>
                );
              })()}
              <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                {groupTableSelected.length} of {modalGroups.length} group{modalGroups.length !== 1 ? 's' : ''} selected
              </Typography>
            </>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setGroupTableDialogOpen(false)}>Cancel</Button>
          <Button onClick={saveGroupTableSelection} variant="contained" disabled={modalGroupsLoading}>
            Save Selection
          </Button>
        </DialogActions>
      </Dialog>

      {/* Test Message Dialog */}
      <Dialog open={testDialogOpen} onClose={() => setTestDialogOpen(false)} maxWidth="sm" fullWidth>
        <DialogTitle>
          Send Test Message - {selectedAccountForTest?.accountName}
        </DialogTitle>
        <DialogContent>
          <Box sx={{ pt: 1 }}>
            <TextField
              fullWidth
              label="Phone Number"
              value={testNumber}
              onChange={(e) => setTestNumber(e.target.value)}
              placeholder="e.g., 1234567890"
              sx={{ mb: 2 }}
              helperText="Enter phone number without country code prefix"
            />
            <TextField
              fullWidth
              multiline
              rows={4}
              label="Message"
              value={testMessage}
              onChange={(e) => setTestMessage(e.target.value)}
            />
          </Box>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setTestDialogOpen(false)}>Cancel</Button>
          <Button
            onClick={sendTestMessage}
            variant="contained"
            disabled={loading || !testNumber.trim() || !testMessage.trim()}
            startIcon={<SendIcon />}
          >
            {loading ? 'Sending...' : 'Send'}
          </Button>
        </DialogActions>
      </Dialog>
    </Box>
  );
};

export default WhatsappAccounts;
